import type { ReactNode } from "react";
import { Icon } from "./Icon";

/**
 * Selectable tile — a card-shaped choice with a leading visual, title and
 * description. Three modes:
 *   - interactive checkbox (multi-select add-ons)
 *   - interactive radio (single-select)
 *   - static (`readOnly`) for "what's included" showcases
 * The optional `trailing` slot holds a price or badge.
 */
export interface OptionTileProps {
  title: ReactNode;
  description?: ReactNode;
  /** Leading visual — a ProductIcon, FamilyIcon, or Icon. */
  icon?: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onToggle?: () => void;
  /** Selection semantics for interactive tiles. Default: checkbox. */
  control?: "checkbox" | "radio";
  /** Render non-interactive (e.g. an "included" item). */
  readOnly?: boolean;
  trailing?: ReactNode;
  className?: string;
}

export function OptionTile({
  title,
  description,
  icon,
  selected = false,
  disabled = false,
  onToggle,
  control = "checkbox",
  readOnly = false,
  trailing,
  className,
}: OptionTileProps) {
  const interactive = !readOnly && !!onToggle;
  const cls = [
    "fd-tile",
    selected && "fd-tile--selected",
    disabled && "fd-tile--disabled",
    readOnly && "fd-tile--static",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const body = (
    <>
      {icon && <span className="fd-tile__icon">{icon}</span>}
      <span className="fd-tile__body">
        <span className="fd-tile__title">{title}</span>
        {description && <span className="fd-tile__desc">{description}</span>}
      </span>
      {trailing && <span className="fd-tile__trailing">{trailing}</span>}
      {(interactive || (readOnly && selected)) && (
        <span
          className={["fd-tile__check", control === "radio" && "fd-tile__check--radio"]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          {selected && <Icon name="check" size={14} />}
        </span>
      )}
    </>
  );

  if (!interactive) {
    return <div className={cls}>{body}</div>;
  }

  return (
    <button
      type="button"
      role={control === "radio" ? "radio" : "checkbox"}
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      className={cls}
      onClick={onToggle}
    >
      {body}
    </button>
  );
}
